import React, { useState, useCallback, useEffect } from "react";
import { Button } from "primereact/button";
import { CommonModal } from "@/core/components/commonModal/CommonModal";
import { TeamMembersList } from "../components/TeamMembersList";
import { InvitationsList } from "../components/InvitationsList";
import { InviteTeamMembers } from "../components/InviteTeamMembers";
import { TeamManagementViewModel } from "../viewmodel/TeamManagementViewModel";
import { InvitationsViewModel } from "../viewmodel/InvitationsViewModel";
import { useToast } from "@/core/hooks/useToast";

interface PartnerMember {
  id: string;
  logo_uri: string;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  description: string;
  job_title: string;
  is_followed_by_me: boolean;
  is_following_me: boolean;
  is_me: boolean;
  is_admin?: boolean;
}

interface InvitationResult {
  email: string;
  status: "success" | "error";
  message: string;
}

interface InvitationResponse {
  results: InvitationResult[];
}

interface TeamTabProps {
  partnerMembers: PartnerMember[];
  refetchOrganization: () => Promise<any>;
  isAdmin: boolean;
}

export const TeamTab: React.FC<TeamTabProps> = ({
  partnerMembers,
  refetchOrganization,
  isAdmin,
}) => {
  const { makeAdmin, removeMember, sendInvitations } =
    TeamManagementViewModel(refetchOrganization);
  const {
    requests,
    invitations,
    loading,
    handleRequestAction,
    withdrawInvitation,
    fetchInvitations,
  } = InvitationsViewModel();
  const toast = useToast();

  // Modals state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showMakeAdminModal, setShowMakeAdminModal] = useState(false);
  const [memberToMakeAdmin, setMemberToMakeAdmin] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Confirm remove member
  const confirmRemoveMember = useCallback(
    (memberId: string, memberName: string) => {
      setMemberToRemove({ id: memberId, name: memberName });
      setShowConfirmModal(true);
    },
    []
  );

  useEffect(() => {
    // console.log("elaaa");
    // console.log(requests, "requests");
    // console.log(invitations, "invitations");
  }, []);

  const handleConfirmRemoval = useCallback(async () => {
    if (memberToRemove) {
      try {
        await removeMember(memberToRemove.id);
        setShowConfirmModal(false);
        setMemberToRemove(null);
      } catch (error) {
        console.error("Failed to remove member:", error);
      }
    }
  }, [memberToRemove, removeMember]);

  const handleCancelRemoval = useCallback(() => {
    setShowConfirmModal(false);
    setMemberToRemove(null);
  }, []);

  // Confirm make admin
  const confirmMakeAdmin = useCallback(
    (memberId: string, memberName: string) => {
      setMemberToMakeAdmin({ id: memberId, name: memberName });
      setShowMakeAdminModal(true);
    },
    []
  );

  const handleConfirmMakeAdmin = useCallback(async () => {
    if (memberToMakeAdmin) {
      try {
        await makeAdmin(memberToMakeAdmin.id);
        setShowMakeAdminModal(false);
        setMemberToMakeAdmin(null);
      } catch (error) {
        console.error("Failed to make member admin:", error);
      }
    }
  }, [memberToMakeAdmin, makeAdmin]);

  const handleCancelMakeAdmin = useCallback(() => {
    setShowMakeAdminModal(false);
    setMemberToMakeAdmin(null);
  }, []);

  // Send invitations
  const handleSendInvitations = useCallback(
    async (emails: string[]) => {
      try {
        const response = await sendInvitations(emails);
        await fetchInvitations();

        // Handle the response and show toast messages
        const invitationResponse = response as InvitationResponse;
        // console.log(response, "invitation response");
        if (
          invitationResponse?.results &&
          Array.isArray(invitationResponse.results)
        ) {
          const successCount = invitationResponse.results.filter(
            (result) => result.status === "success"
          ).length;
          const errorResults = invitationResponse.results.filter(
            (result) => result.status === "error"
          );

          // Show success message for successful invitations
          if (successCount > 0) {
            invitationResponse?.results &&
              Array.isArray(invitationResponse.results) &&
              toast.showSuccess(invitationResponse?.results[0].message);
          }

          // Show error messages for failed invitations
          errorResults.forEach((result) => {
            toast.showError(`${result.email}: ${result.message}`);
          });
        } else {
          // Fallback success message if response structure is different
          toast.showSuccess("Invitations sent successfully!");
        }
      } catch (error) {
        console.error("Failed to send invitations:", error);
        toast.showError("Failed to send invitations. Please try again.");
        throw error;
      }
    },
    [sendInvitations, fetchInvitations, toast]
  );

  return (
    <div className="team-section">
      <h3>Team Management</h3>
      <p>Manage your organization team members and their roles.</p>

      <div className="team-members">
        {/* Requests to join section */}
        {isAdmin && requests.length > 0 && (
          <>
            <section>
              <div className="texts">
                <div className="title">Requests to join</div>
                <div className="subtitle">
                  Accept or decline user requests to join your organization
                </div>
              </div>
              <div className="content">
                <InvitationsList
                  invitations={requests}
                  loading={loading}
                  onAccept={(actionId) =>
                    handleRequestAction(actionId, "accept")
                  }
                  onDeny={(actionId) => handleRequestAction(actionId, "deny")}
                  onWithdraw={withdrawInvitation}
                  isAdmin={isAdmin}
                />
              </div>
            </section>
          </>
        )}
        {isAdmin && invitations.length > 0 && (
          <>
            <section>
              <div className="texts">
                <div className="title">Invitations to join</div>
                <div className="subtitle">
                  Manage pending invitations for users to join your organization
                </div>
              </div>
              <div className="content">
                <InvitationsList
                  invitations={invitations}
                  loading={loading}
                  onAccept={(actionId) =>
                    handleRequestAction(actionId, "accept")
                  }
                  onDeny={(actionId) => handleRequestAction(actionId, "deny")}
                  onWithdraw={withdrawInvitation}
                  isAdmin={isAdmin}
                />
              </div>
            </section>
          </>
        )}

        {/* Invite team members section */}
        {isAdmin && (
          <section>
            <div className="texts">
              <div className="title">Invite team members</div>
              <div className="subtitle">
                Get your projects running faster by inviting your team to
                collaborate
              </div>
            </div>
            <InviteTeamMembers
              onSendInvitations={handleSendInvitations}
              isAdmin={isAdmin}
            />
          </section>
        )}

        {/* Manage existing team */}
        <section>
          <div className="texts">
            <div className="title">Team members</div>
            <div className="subtitle">
              Manage your existing team and change roles/permissions
            </div>
          </div>
          <div className="content">
            <TeamMembersList
              members={partnerMembers}
              onRemoveMember={confirmRemoveMember}
              onMakeAdmin={confirmMakeAdmin}
              isAdmin={isAdmin}
            />
          </div>
        </section>
      </div>

      {/* Confirm Remove Member Modal */}
      <CommonModal
        isOpen={showConfirmModal}
        onClose={handleCancelRemoval}
        width={420}
        height={320}
        icon="icon-warning"
        actions={
          <div
            style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}
          >
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={handleCancelRemoval}
            />
            <Button
              label="Remove"
              severity="danger"
              onClick={handleConfirmRemoval}
            />
          </div>
        }
      >
        <div style={{ padding: "1rem" }}>
          <p>
            Are you sure you want to remove{" "}
            <strong>{memberToRemove?.name}</strong> from your organization?
          </p>
          <p>They will not be a member of the organisation anymore.</p>
        </div>
      </CommonModal>

      {/* Confirm Make Admin Modal */}
      <CommonModal
        isOpen={showMakeAdminModal}
        onClose={handleCancelMakeAdmin}
        width={420}
        height={320}
        icon="icon-info"
        actions={
          <div
            style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}
          >
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={handleCancelMakeAdmin}
            />
            <Button label="Make Admin" onClick={handleConfirmMakeAdmin} />
          </div>
        }
      >
        <div style={{ padding: "1rem" }}>
          <p>
            Are you sure you want to make{" "}
            <strong>{memberToMakeAdmin?.name}</strong> organisation admin?
          </p>
          <p>You will not be able to edit your organisation anymore.</p>
        </div>
      </CommonModal>
    </div>
  );
};
